/**
 * Admin analytics external-links card.
 *
 * Drop-in tile for the Admin Dashboard that provides one-click
 * access to the three analytics/search tools CollectorUp uses:
 *   · Google Analytics
 *   · Microsoft Clarity
 *   · Google Search Console
 *
 * URLs are configurable via env vars so Gil can point them at the
 * exact property/project when he's set them up:
 *   REACT_APP_GA4_DASHBOARD_URL      (optional, default: analytics.google.com/analytics)
 *   REACT_APP_CLARITY_DASHBOARD_URL  (optional, default: clarity.microsoft.com)
 *   REACT_APP_GSC_DASHBOARD_URL      (optional, default: search.google.com/search-console)
 *
 * The card NEVER embeds credentials and NEVER attempts to iframe
 * the tools (all three of them block cross-origin embedding).
 */
import React from "react";
import { BarChart3, Activity, Search, ExternalLink } from "lucide-react";

const LINKS = [
  {
    key: "ga4",
    label: "Google Analytics",
    desc: "Traffic, page views, conversion events",
    icon: BarChart3,
    href: (process.env.REACT_APP_GA4_DASHBOARD_URL || "").trim()
      || "https://analytics.google.com/analytics/web/",
    testid: "admin-analytics-link-ga4",
  },
  {
    key: "clarity",
    label: "Microsoft Clarity",
    desc: "Session recordings and heatmaps",
    icon: Activity,
    href: (process.env.REACT_APP_CLARITY_DASHBOARD_URL || "").trim()
      || "https://clarity.microsoft.com/",
    testid: "admin-analytics-link-clarity",
  },
  {
    key: "gsc",
    label: "Google Search Console",
    desc: "Search performance, indexing, sitemaps",
    icon: Search,
    href: (process.env.REACT_APP_GSC_DASHBOARD_URL || "").trim()
      || "https://search.google.com/search-console",
    testid: "admin-analytics-link-gsc",
  },
];

export default function AdminAnalyticsLinks() {
  return (
    <section
      className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 md:p-5"
      data-testid="admin-analytics-links-card"
    >
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
        Analytics
      </div>
      <h2 className="font-display text-lg md:text-xl mt-0.5">
        Open Analytics Tools
      </h2>
      <p className="text-xs text-[var(--smoke)] mt-1">
        Each link opens in a new tab. CollectorUp does not embed these
        tools directly.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        {LINKS.map((l) => {
          const Icon = l.icon;
          return (
            <a
              key={l.key}
              href={l.href}
              target="_blank" rel="noopener noreferrer"
              className="block rounded-xl border-2 border-[var(--stone)] p-3 hover:border-[var(--ink)] transition"
              data-testid={l.testid}
            >
              <div className="flex items-start justify-between gap-2">
                <Icon size={20} className="text-[var(--panini-red)]" />
                <ExternalLink size={14} className="text-[var(--smoke)]" />
              </div>
              <div className="mt-2 font-bold text-sm">{l.label}</div>
              <div className="text-xs text-[var(--smoke)] mt-0.5">{l.desc}</div>
            </a>
          );
        })}
      </div>
    </section>
  );
}
